export type KeyTreeNode = {
    segment: string;
    path: string;
    isKey: boolean;
    children: KeyTreeNode[];
};

export type KeyTreeRow = {
    node: KeyTreeNode;
    depth: number;
    hasChildren: boolean;
};

export const buildKeyTree = (keys: string[]): KeyTreeNode[] => {
    const roots: KeyTreeNode[] = [];
    const byPath = new Map<string, KeyTreeNode>();

    const ensure = (path: string, segment: string): KeyTreeNode => {
        const existing = byPath.get(path);
        if (existing) return existing;

        const node: KeyTreeNode = { segment, path, isKey: false, children: [] };
        byPath.set(path, node);

        const dot = path.lastIndexOf(".");
        if (dot === -1) {
            roots.push(node);
            return node;
        }

        const parentPath = path.slice(0, dot);
        const parentSegment = parentPath.slice(parentPath.lastIndexOf(".") + 1);
        ensure(parentPath, parentSegment).children.push(node);
        return node;
    };

    for (const key of keys) {
        if (!key) continue;
        const parts = key.split(".");
        let path = "";
        for (const part of parts) {
            path = path ? `${path}.${part}` : part;
            ensure(path, part);
        }
        ensure(key, parts[parts.length - 1]).isKey = true;
    }

    const sortNodes = (nodes: KeyTreeNode[]) => {
        nodes.sort((a, b) => a.segment.localeCompare(b.segment));
        nodes.forEach((node) => sortNodes(node.children));
    };
    sortNodes(roots);
    return roots;
};

export const ancestorsOfPath = (path: string): string[] => {
    const parts = path.split(".");
    const ancestors: string[] = [];
    let current = "";
    for (let i = 0; i < parts.length - 1; i++) {
        current = current ? `${current}.${parts[i]}` : parts[i];
        ancestors.push(current);
    }
    return ancestors;
};

export const flattenKeyTree = (
    roots: KeyTreeNode[],
    { expanded, search, expandAll }: { expanded: Set<string>; search: string; expandAll?: boolean },
): KeyTreeRow[] => {
    const needle = search.trim().toLowerCase();

    const onPathToMatch = new Set<string>();
    if (needle) {
        const mark = (node: KeyTreeNode): boolean => {
            const childMatched = node.children.map(mark).filter(Boolean).length > 0;
            const matched =
                node.path.toLowerCase().includes(needle) ||
                node.segment.toLowerCase().includes(needle) ||
                childMatched;
            if (matched) onPathToMatch.add(node.path);
            return matched;
        };
        roots.forEach(mark);
    }

    const rows: KeyTreeRow[] = [];
    const walk = (node: KeyTreeNode, depth: number) => {
        const children = node.children.filter(
            (child) => !needle || onPathToMatch.has(child.path),
        );
        rows.push({ node, depth, hasChildren: children.length > 0 });
        if (needle || expandAll || expanded.has(node.path)) {
            children.forEach((child) => walk(child, depth + 1));
        }
    };

    roots
        .filter((node) => !needle || onPathToMatch.has(node.path))
        .forEach((node) => walk(node, 0));

    return rows;
};
